import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { ColorRing } from "react-loader-spinner";
import Lottie from "react-lottie";
import * as animationData from "../assets/location.json";
import axios from "../axios";
import { ThemeContext } from "../context/ThemeContext";
import { useBusinessNameContext } from "../context/BusinessName";
import Footer from "./Footer";
import { Outlet } from "react-router-dom";

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

const Layout = () => {
  const location = useLocation();
  const [hideFooter, setHideFooter] = useState(false);
  const { theme, setTheme } = useContext(ThemeContext);
  const { setBusinessName } = useBusinessNameContext();
  const { t } = useTranslation();

  // Modal states
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showRoomNumberModal, setShowRoomNumberModal] = useState(false);
  const [showLocationInstructions, setShowLocationInstructions] =
    useState(false);
  const room_qr = localStorage.getItem("room_uuid_before_redirect");
  const region = process.env.REACT_APP_SERVER_REGION;
  // Form states
  const [inputRoomNumber, setInputRoomNumber] = useState("");
  const [roomModalError, setRoomModalError] = useState("");
  const [locationModalError, setLocationModalError] = useState("");
  const [locationInstructions, setLocationInstructions] = useState("");

  // Loading states
  const [loadingAllowLocation, setLoadingAllowLocation] = useState(false);
  const [submittingRoomNumber, setSubmittingRoomNumber] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  // Data states
  const [platformType, setPlatformType] = useState();
  const businessLocationRef = useRef(null);

  // Get room_uuid from URL
  const rawPath = location.pathname;
  const pathSegments = rawPath.split("/");
  const room_uuid = pathSegments[1];

  // Dine-in restaurant menu route: /:room_uuid/rstauran-menu-categories/:id
  const isDinein = pathSegments[2] === "rstauran-menu-categories";
  const restaurant_uuid = isDinein ? pathSegments[3] : undefined;

  const lang = localStorage.getItem("lang");

  // Platform detection
  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    let type = "unknown";

    if (/android/i.test(ua)) {
      type = "Android";
    } else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
      type = "iOS";
    }

    setPlatformType(type);
  }, []);

  // Footer visibility logic
  useEffect(() => {
    const shouldHideFooter =
      location.pathname.includes("/RestaurantSubMenu") ||
      location.pathname.includes("/PharmacySubMenu") ||
      location.pathname.includes("/FlowersSubMenu") ||
      location.pathname.includes("/SupermarketSubMenu") ||
      location.pathname.includes("/restaurant-menu/categories") ||
      location.pathname.includes("/restaurants/search") ||
      location.pathname.includes("/restaurant-menu") ||
      location.pathname.includes("/rstauran-menu-categories") ||
      location.pathname.includes("/rstauran-menu-subcategories") ||
      location.pathname.includes("/rstauran-menu-items") ||
      location.pathname.includes("/Checkout-dining") ||
      location.pathname.includes("/qr-scan") ||
      location.pathname.includes("/RedirectTaxi") ||
      location.pathname.includes("/scan-qr-code") ||
      location.pathname.includes("/not-found") ||
      location.pathname.includes("/food-and-beverages") ||
      location.pathname.includes("/cart") ||
      location.pathname.includes("/search") ||
      location.pathname.includes("/hotel-services") ||
      location.pathname.includes("/qrcode") ||
      location.pathname.includes("/restaurant/combo-meals") ||
      location.pathname.includes("/taxi-yango") ||
      location.search.includes("Restaurant&in_dining") ||
      location.pathname.includes("open-checkout") ||
      theme;

    setHideFooter(shouldHideFooter);
  }, [location.pathname, location.search, theme]);

  // Initialize app data and check for modals
  useEffect(() => {
    const initializeApp = async () => {
      if (!room_uuid) return;

      setDataLoading(true);
      try {
        const res = await axios.get(`/${room_uuid}/qr-code?source=footer`, {
          headers: {
            lang: lang,
          },
          params: isDinein ? { restaurant_uuid } : {},
        });
        const data = res.data.data;
        localStorage.setItem("active_flexipass", data?.active_flexipass);
        // Store data in localStorage
        // localStorage.setItem("is_meeting_room", data?.is_meeting_room);
        localStorage.setItem("room_number", data?.room_number);
        localStorage.setItem("currency", data?.currency || "AED");
        localStorage.setItem("color", data?.color);
        localStorage.setItem("tax_inclusive", data?.tax_inclusive);
        localStorage.setItem("show_prices", data?.show_prices);
        localStorage.setItem("has_extra_services", data?.has_extra_services);
        localStorage.setItem("new_design", data?.use_new_design);
        localStorage.setItem("businessType", data.businessType);
        localStorage.setItem("countryCode", data?.country_code || "ae");
        localStorage.setItem("business_name", data?.business_name);
        localStorage.setItem("city", data?.city);
        localStorage.setItem(
          "get_open_check_available",
          data?.get_open_check_available,
        );

        const sessionInfo = {
          "Room UUID": room_qr,
          "Session ID": room_uuid,
          "Business Name": data?.business_name,
          "Room Number": data?.room_number,
          "Location Type": data?.room_type,
          "Business ID": data?.business_id,
          "Server Region": region,
          Language: lang,
          Platform: "Web",
          utm_source: "",
          utm_medium: "",
          utm_campaign: "",

          // OS: navigator.platform,
        };
        localStorage.setItem("mixpannel_data", JSON.stringify(sessionInfo));
        // Update context
        setTheme(data?.use_new_design);
        setBusinessName(data?.business_name);

        // Set CSS variables
        if (data?.color) {
          document.documentElement.style.setProperty("--color", data?.color);
          document.documentElement.style.setProperty(
            "--color_t",
            `${data?.color}b3`,
          );
          document.documentElement.style.setProperty(
            "--header_color",
            data?.header_color,
          );
        }

        // Show modals based on requirements
        if (data?.validate_location && data?.business_location) {
          businessLocationRef.current = data.business_location;
          setShowLocationModal(true);
        }

        if (data?.room_number_required) {
          setShowRoomNumberModal(true);
        }
      } catch (err) {
        console.error("Error initializing app data", err);
      } finally {
        setDataLoading(false);
      }
    };

    initializeApp();
  }, [room_uuid, restaurant_uuid, setTheme, setBusinessName]);

  // Handle location permission
  const handleAllowLocation = () => {
    setLoadingAllowLocation(true);
    setLocationModalError("");

    if (!navigator.geolocation) {
      setLocationModalError(t("location not supported"));
      setLoadingAllowLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;
        const business = businessLocationRef.current;

        if (!business) {
          setLocationModalError(t("location missing"));
          setLoadingAllowLocation(false);
          return;
        }

        // Calculate distance using Haversine formula
        const toRad = (value) => (value * Math.PI) / 180;
        const R = 6371; // Earth's radius in km

        const dLat = toRad(business.lat - userLat);
        const dLon = toRad(business.long - userLng);
        const lat1 = toRad(userLat);
        const lat2 = toRad(business.lat);

        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.sin(dLon / 2) *
            Math.sin(dLon / 2) *
            Math.cos(lat1) *
            Math.cos(lat2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;

        if (distance > 0.5) {
          setLocationModalError(t("far location"));
        } else {
          setShowLocationModal(false);
        }
        setLoadingAllowLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        setShowLocationInstructions(true);
        setLoadingAllowLocation(false);
        setLocationModalError(t("location failed access"));

        if (platformType === "iOS") {
          setLocationInstructions(t("allow location guideline iOS"));
        } else {
          setLocationInstructions(t("allow location guideline android"));
        }
      },
    );
  };

  // Handle room number submission
  const handleRoomNumberSubmit = async () => {
    if (!inputRoomNumber) {
      setRoomModalError(t("Please enter a room number"));
      return;
    }

    setSubmittingRoomNumber(true);
    setRoomModalError("");

    try {
      const res = await axios.post(`/${room_uuid}/validate-room-number`, {
        room_number: inputRoomNumber,
      });

      if (res.data.data.room) {
        window.location.replace(`/${res.data.data.room}/qr-code`);
      } else {
        setRoomModalError(t("Invalid room number"));
        setSubmittingRoomNumber(false);
      }
    } catch (error) {
      console.error("Room validation error:", error);
      setRoomModalError(t("Failed to validate room number"));
      setSubmittingRoomNumber(false);
    }
  };

  return (
    <>
      <Outlet />
      {!hideFooter && <Footer />}

      {/* Location Permission Modal */}
      <Modal
        className={`x ${theme ? "food-modal" : ""}`}
        show={showLocationModal}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Body style={{ padding: "2rem", height: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Lottie options={defaultOptions} height={200} width={200} />
          </div>
          <h2
            style={{
              marginTop: "1rem",
              fontSize: "18px",
              textAlign: "center",
              color: theme ? "white" : "black",
            }}
          >
            {t("allow location")}
          </h2>
          <p
            className="text-xl"
            style={{
              color: "gray",
              fontSize: "12px",
              textAlign: "center",
            }}
          >
            {t("location req")}
          </p>

          {locationModalError ? (
            <p className="text-danger mt-3" style={{ textAlign: "center" }}>
              {locationModalError}
              {showLocationInstructions && (
                <>
                  <br />
                  <br />
                  <Trans
                    i18nKey="allow location guideline iOS"
                    components={{ bold: <strong /> }}
                  />
                </>
              )}
            </p>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <button
                onClick={handleAllowLocation}
                disabled={loadingAllowLocation}
                className="reg-btn"
                style={{
                  width: "100%",
                  textAlign: "center",
                  marginBottom: "-1px",
                  cursor: "pointer",
                  opacity: loadingAllowLocation ? "70%" : "100%",
                }}
              >
                {t("request access")}
              </button>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Room Number Modal */}
      <Modal
        className={`x ${theme ? "food-modal" : ""}`}
        show={!showLocationModal && showRoomNumberModal}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Body style={{ padding: "2rem", height: "100%" }}>
          <h2
            style={{
              textAlign: "center",
              color: theme ? "white" : "black",
            }}
          >
            {t("enter your room number")}
          </h2>
          <input
            type="text"
            className="form-control my-3"
            placeholder={t("room number")}
            value={inputRoomNumber}
            onChange={(e) => setInputRoomNumber(e.target.value)}
          />
          {roomModalError && (
            <p className="text-danger" style={{ textAlign: "center" }}>
              {roomModalError}
            </p>
          )}
          <div
            style={{
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <button
              className="reg-btn"
              onClick={handleRoomNumberSubmit}
              disabled={submittingRoomNumber}
              style={{ opacity: submittingRoomNumber ? "0.7" : "1" }}
            >
              {submittingRoomNumber ? (
                <div
                  style={{
                    textAlign: "center",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <ColorRing
                    visible={true}
                    height="20"
                    width="20"
                    ariaLabel="color-ring-loading"
                    wrapperStyle={{
                      textAlign: "center",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    wrapperClass="color-ring-wrapper"
                    colors={["#000"]}
                  />
                </div>
              ) : (
                t("verify")
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Layout;
